import { FormEvent, forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useReverification, useUser } from '@clerk/clerk-react';
import { isReverificationCancelledError } from '@clerk/clerk-react/errors';
import { AccessLevel } from '../../types/access';
import {
  ACCOUNT_SETUP_COMPLETED_KEY,
  PROFILE_CLINICAL_AREAS,
  PROFESSION_OPTIONS,
  WORK_EMAIL_ONBOARDING_DISMISSED_KEY,
  isAccountSetupCompleted,
  readProfileExtras,
  type UserProfileExtras,
} from '../../types/userProfile';
import { isOntarioHospitalDomain, isOrbconAdminDomain } from '../../utils/accessLevel';
import { clinicalAreaLabels } from '../../utils/metadataHelpers';
import { useAccessLevel } from '../../hooks/useAccessLevel';
import { useHospitalOhpDomains } from '../../hooks/useHospitalOhpDomains';
import AccountReverificationModal, { type ReverificationPurpose } from './AccountReverificationModal';
import Button from '../ui/Button';
import Dropdown from '../ui/Dropdown';
import Input from '../ui/Input';
import SelectableChips from '../ui/SelectableChips';
import TopicInterestPicker from '../ui/TopicInterestPicker';
import VerificationCodeInput from '../ui/VerificationCodeInput';

type SetupStep = 'work-email' | 'role' | 'interests';

const STEP_COPY: Record<SetupStep, { heading: string; body: string }> = {
  'work-email': {
    heading: 'Work email',
    body: 'Add and verify an Ontario healthcare professional email to unlock resources and features for Ontario hospital professionals, including resources specific to your site.',
  },
  role: {
    heading: 'Your role',
    body: 'This helps My ORBCoN speak to the work you do.',
  },
  interests: {
    heading: 'Your interests',
    body: 'Choose clinical areas and topics you want to see more of.',
  },
};

function clerkErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'errors' in error) {
    const errors = (error as { errors?: Array<{ longMessage?: string; message?: string }> }).errors;
    const first = errors?.[0];
    const message = first?.longMessage || first?.message;
    if (message && /taken|already exists|already been taken/i.test(message)) {
      return 'That email is already on another ORBCoN account. Sign in with that account, or remove the email there first.';
    }
    if (message) return message;
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong. Please try again.';
}

function isEmailVerified(status: string | null | undefined): boolean {
  return status === 'verified';
}

const clinicalAreaOptions = PROFILE_CLINICAL_AREAS.map((value) => ({
  value,
  label: clinicalAreaLabels[value],
}));

type AccountSetupFormProps = {
  onFinished: () => void;
};

export type AccountSetupFormHandle = {
  dismiss: () => void;
};

const AccountSetupForm = forwardRef<AccountSetupFormHandle, AccountSetupFormProps>(
  function AccountSetupForm({ onFinished }, ref) {
  const { user } = useUser();
  const { accessLevel, isLoading: accessLoading } = useAccessLevel();
  const { domains: hospitalDomains } = useHospitalOhpDomains();
  const [step, setStep] = useState<SetupStep | null>(null);
  const [extras, setExtras] = useState<UserProfileExtras>(() =>
    readProfileExtras(user?.unsafeMetadata)
  );
  const [newWorkEmail, setNewWorkEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingEmailId, setPendingEmailId] = useState<string | null>(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [reverification, setReverification] = useState<{
    level?: string;
    purpose: ReverificationPurpose;
    onComplete: () => void;
    onCancel: () => void;
  } | null>(null);
  const primedUnverifiedRef = useRef(false);
  const finishedRef = useRef(false);

  const onNeedsReverification = useCallback(
    ({
      complete,
      cancel,
      level,
    }: {
      complete: () => void;
      cancel: () => void;
      level?: string;
    }) => {
      setReverification({
        level,
        purpose: 'add-work-email',
        onComplete: () => {
          complete();
          setReverification(null);
        },
        onCancel: () => {
          cancel();
          setReverification(null);
        },
      });
    },
    []
  );
  const createEmailAddress = useReverification((email: string) => {
    if (!user) {
      throw new Error('You need to be signed in.');
    }
    return user.createEmailAddress({ email });
  }, { onNeedsReverification });

  const needsWorkEmail = accessLevel === AccessLevel.Public;
  const steps = useMemo<SetupStep[]>(
    () => (needsWorkEmail ? ['work-email', 'role', 'interests'] : ['role', 'interests']),
    [needsWorkEmail]
  );
  const currentStep = step && steps.includes(step) ? step : steps[0];
  const stepIndex = Math.max(0, steps.indexOf(currentStep));
  const isLastStep = stepIndex === steps.length - 1;
  const pendingAddress = user?.emailAddresses.find((address) => address.id === pendingEmailId);
  const busy = emailBusy || saving;

  useEffect(() => {
    if (accessLoading) return;
    setStep((current) => {
      if (current === 'work-email' && !needsWorkEmail) return 'role';
      if (!current) return needsWorkEmail ? 'work-email' : 'role';
      return current;
    });
  }, [accessLoading, needsWorkEmail]);

  useEffect(() => {
    if (currentStep !== 'work-email') {
      primedUnverifiedRef.current = false;
      return;
    }
    if (primedUnverifiedRef.current || !user) return;
    primedUnverifiedRef.current = true;
    const unverified = user.emailAddresses.find(
      (address) => !isEmailVerified(address.verification?.status)
    );
    if (unverified) {
      setPendingEmailId(unverified.id);
    }
  }, [currentStep, user]);

  async function persistProfileFields(options?: { completed?: boolean }) {
    if (!user) return;
    const previous = { ...(user.unsafeMetadata as Record<string, unknown>) };
    await user.update({
      unsafeMetadata: {
        ...previous,
        namePrefix: extras.namePrefix || '',
        jobTitle: extras.jobTitle?.trim() || '',
        profession: extras.profession || '',
        organizationIds: extras.organizationIds || [],
        clinicalAreas: extras.clinicalAreas || [],
        topics: extras.topics || [],
        ...(options?.completed
          ? {
              [ACCOUNT_SETUP_COMPLETED_KEY]: true,
              [WORK_EMAIL_ONBOARDING_DISMISSED_KEY]: true,
            }
          : {}),
      },
    });
  }

  function finish() {
    if (finishedRef.current) return;
    finishedRef.current = true;
    onFinished();
  }

  useEffect(() => {
    if (user && isAccountSetupCompleted(user.unsafeMetadata)) {
      finish();
    }
  }, [user]);

  async function finishLater() {
    try {
      await persistProfileFields({ completed: true });
    } catch {
      // Close anyway so the flow cannot trap the session.
    }
    finish();
  }

  useImperativeHandle(ref, () => ({
    dismiss: () => {
      void finishLater();
    },
  }));

  async function goNext() {
    setSaving(true);
    setSetupError(null);
    try {
      if (isLastStep) {
        await persistProfileFields({ completed: true });
        finish();
        return;
      }
      await persistProfileFields();
      setStep(steps[stepIndex + 1]);
    } catch (err) {
      setSetupError(clerkErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleAddWorkEmail(event?: FormEvent) {
    event?.preventDefault();
    if (!user) return;
    const nextEmail = newWorkEmail.trim();
    if (!nextEmail) {
      setEmailError('Enter a work email.');
      return;
    }
    setEmailBusy(true);
    setEmailError(null);
    setStatusMessage(null);
    try {
      const created = await createEmailAddress(nextEmail);
      if (!created) {
        throw new Error('Could not add that email.');
      }
      await created.prepareVerification({ strategy: 'email_code' });
      setPendingEmailId(created.id);
      setVerificationCode('');
      setStatusMessage(`Verification code sent to ${nextEmail}.`);
    } catch (err) {
      if (isReverificationCancelledError(err)) {
        return;
      }
      setEmailError(clerkErrorMessage(err));
    } finally {
      setEmailBusy(false);
    }
  }

  async function sendVerificationCode(emailId: string) {
    if (!user) return;
    const address = user.emailAddresses.find((item) => item.id === emailId);
    if (!address) {
      setEmailError('That email is no longer on this account.');
      return;
    }
    setEmailBusy(true);
    setEmailError(null);
    try {
      await address.prepareVerification({ strategy: 'email_code' });
      setPendingEmailId(address.id);
      setVerificationCode('');
      setStatusMessage(`Verification code sent to ${address.emailAddress}.`);
    } catch (err) {
      setEmailError(clerkErrorMessage(err));
    } finally {
      setEmailBusy(false);
    }
  }

  async function handleVerifyWorkEmail(code = verificationCode) {
    if (!user || !pendingEmailId) return;
    const address = user.emailAddresses.find((item) => item.id === pendingEmailId);
    if (!address) {
      setEmailError('That email is no longer on this account.');
      return;
    }
    const trimmed = code.trim();
    if (trimmed.length !== 6) return;
    setEmailBusy(true);
    setEmailError(null);
    try {
      await address.attemptVerification({ code: trimmed });
      await user.reload();
      setPendingEmailId(null);
      setVerificationCode('');
      setNewWorkEmail('');
      const hospitalMatch =
        isOrbconAdminDomain(address.emailAddress) ||
        isOntarioHospitalDomain(address.emailAddress, hospitalDomains);
      if (hospitalMatch) {
        setStep('role');
      } else {
        setStatusMessage(
          'That email is verified, but it is not on the hospital domain list. Add a hospital work email, or continue without one.'
        );
      }
    } catch (err) {
      setEmailError(clerkErrorMessage(err));
    } finally {
      setEmailBusy(false);
    }
  }

  if (!user || accessLoading) {
    return <p className="text-sm text-gray-600">Loading your account…</p>;
  }

  const copy = STEP_COPY[currentStep];

  return (
    <>
      <div className="space-y-4">
        <p className="text-sm text-gray-500">
          Step {stepIndex + 1} of {steps.length}
        </p>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{copy.heading}</h3>
          <p className="text-sm text-gray-600 mt-1">{copy.body}</p>
        </div>

        {currentStep === 'work-email' && (
          <div className="space-y-4">
            {statusMessage && (
              <p className="text-sm text-gray-600" role="status">
                {statusMessage}
              </p>
            )}
            {pendingAddress ? (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">
                  Enter the code we sent to {pendingAddress.emailAddress}.
                </p>
                <VerificationCodeInput
                  value={verificationCode}
                  onChange={setVerificationCode}
                  onComplete={(code) => void handleVerifyWorkEmail(code)}
                  error={emailError ?? undefined}
                  disabled={busy}
                />
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    variant="tertiary"
                    className="min-h-[44px]"
                    disabled={busy}
                    onClick={() => void sendVerificationCode(pendingAddress.id)}
                  >
                    Resend code
                  </Button>
                  <Button
                    type="button"
                    variant="tertiary"
                    className="min-h-[44px]"
                    disabled={busy}
                    onClick={() => {
                      setPendingEmailId(null);
                      setVerificationCode('');
                      setEmailError(null);
                    }}
                  >
                    Use a different email
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={(event) => void handleAddWorkEmail(event)} className="space-y-3">
                <Input
                  label="Work email"
                  type="email"
                  value={newWorkEmail}
                  onChange={(event) => setNewWorkEmail(event.target.value)}
                  autoComplete="email"
                  required
                  error={emailError ?? undefined}
                />
                <Button
                  type="submit"
                  className="min-h-[44px] w-full"
                  disabled={busy || !newWorkEmail.trim()}
                >
                  {emailBusy ? 'Sending…' : 'Send verification code'}
                </Button>
              </form>
            )}
          </div>
        )}

        {currentStep === 'role' && (
          <div className="space-y-4">
            <Dropdown
              label="Profession"
              options={PROFESSION_OPTIONS}
              value={extras.profession || ''}
              placeholder="Select a profession"
              onChange={(value) => setExtras((current) => ({ ...current, profession: value }))}
            />
            <Input
              label="Job title"
              value={extras.jobTitle || ''}
              onChange={(event) =>
                setExtras((current) => ({ ...current, jobTitle: event.target.value }))
              }
              autoComplete="organization-title"
              placeholder="Transfusion safety officer"
            />
          </div>
        )}

        {currentStep === 'interests' && (
          <div className="space-y-6">
            <SelectableChips
              label="Clinical areas"
              hint="Select every area that applies."
              options={clinicalAreaOptions}
              value={extras.clinicalAreas || []}
              onChange={(value) =>
                setExtras((current) => ({
                  ...current,
                  clinicalAreas: PROFILE_CLINICAL_AREAS.filter((area) => value.includes(area)),
                }))
              }
            />
            <TopicInterestPicker
              value={extras.topics || []}
              onChange={(topics) => setExtras((current) => ({ ...current, topics }))}
            />
          </div>
        )}

        {setupError && (
          <p className="text-sm text-error" role="alert">
            {setupError}
          </p>
        )}

        <div className="flex flex-col gap-3 pt-2">
          {currentStep !== 'work-email' && (
            <Button
              type="button"
              className="min-h-[44px] w-full"
              disabled={busy}
              onClick={() => void goNext()}
            >
              {saving ? 'Saving…' : isLastStep ? 'Finish setup' : 'Continue'}
            </Button>
          )}
          {currentStep === 'work-email' && (
            <Button
              type="button"
              variant="secondary"
              className="min-h-[44px] w-full"
              disabled={busy}
              onClick={() => void goNext()}
            >
              Continue without a work email
            </Button>
          )}
          <Button
            type="button"
            variant="tertiary"
            className="min-h-[44px] w-full"
            disabled={busy}
            onClick={() => void finishLater()}
          >
            I'll do this later
          </Button>
        </div>
      </div>
      <AccountReverificationModal
        isOpen={Boolean(reverification)}
        level={reverification?.level}
        purpose={reverification?.purpose}
        preferredEmailAddressId={user.primaryEmailAddressId}
        onComplete={() => reverification?.onComplete()}
        onCancel={() => reverification?.onCancel()}
      />
    </>
  );
});

export default AccountSetupForm;
